import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, Bot, User, Loader2, Wrench, CheckCircle2, XCircle } from 'lucide-react';
import { chatWithHermes } from '@/lib/platformAdminApi';
import { toast } from 'sonner';

const HermesChat = ({ initialMessage = null }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (initialMessage) {
      handleSendMessage(initialMessage);
    }
  }, []);

  useEffect(() => {
    // Auto-scroll to bottom
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSendMessage = async (messageText = input) => {
    if (!messageText.trim() || loading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await chatWithHermes(messageText);

      if (response.success && response.agent_run) {
        const hermesMessage = {
          id: Date.now() + 1,
          type: 'hermes',
          content: response.agent_run.final_response,
          tool_calls: response.agent_run.tool_calls || [],
          outcome: response.agent_run.outcome,
          duration: response.agent_run.total_duration_ms,
          timestamp: response.agent_run.timestamp
        };

        setMessages(prev => [...prev, hermesMessage]);
      } else {
        throw new Error('Invalid response from Hermes');
      }
    } catch (error) {
      console.error('Error chatting with Hermes:', error);
      toast.error('Failed to communicate with Hermes');

      const errorMessage = {
        id: Date.now() + 1,
        type: 'error',
        content: error.message || 'Failed to get response from Hermes',
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="border-b bg-gradient-to-r from-amber-50 to-yellow-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100">
              <Bot className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <CardTitle className="text-xl">Hermes</CardTitle>
              <CardDescription>Platform Administration Agent</CardDescription>
            </div>
          </div>
          <Badge variant="outline" className="bg-white">
            {loading ? 'Thinking...' : 'Online'}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col p-0">
        {/* Messages Area */}
        <ScrollArea className="flex-1 p-4" ref={scrollRef}>
          <div className="space-y-4" data-testid="hermes-messages">
            {messages.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <Bot className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p className="text-lg font-medium mb-2">Chat with Hermes</p>
                <p className="text-sm">Ask about platform metrics, hotel enrollment, system health, or any admin task</p>
                <div className="mt-6 space-y-2">
                  <p className="text-xs text-gray-400">Try asking:</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSendMessage('Check system health')}
                      className="text-xs"
                    >
                      Check system health
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSendMessage('Show platform metrics')}
                      className="text-xs"
                    >
                      Show platform metrics
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSendMessage('Who are the top performers?')}
                      className="text-xs"
                    >
                      Top performers
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex gap-3 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`message-${message.type}`}
              >
                {message.type !== 'user' && (
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                      <Bot className="h-5 w-5 text-amber-600" />
                    </div>
                  </div>
                )}

                <div className={`flex-1 max-w-[80%] ${message.type === 'user' ? 'text-right' : ''}`}>
                  <div
                    className={`rounded-lg p-4 ${
                      message.type === 'user'
                        ? 'bg-blue-600 text-white ml-auto'
                        : message.type === 'error'
                        ? 'bg-red-50 border border-red-200'
                        : 'bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <div className="prose prose-sm max-w-none">
                      <pre className="whitespace-pre-wrap font-sans text-sm">
                        {message.content}
                      </pre>
                    </div>

                    {/* Tool Calls */}
                    {message.tool_calls && message.tool_calls.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                        <p className="text-xs font-semibold text-gray-600">Tool Calls:</p>
                        {message.tool_calls.map((tool, idx) => (
                          <div key={idx} className="bg-white rounded p-2 text-xs">
                            <div className="flex items-center gap-2 mb-1">
                              {tool.error ? (
                                <XCircle className="h-3 w-3 text-red-500" />
                              ) : (
                                <CheckCircle2 className="h-3 w-3 text-green-500" />
                              )}
                              <code className="font-mono font-semibold">{tool.tool_name}</code>
                              <Badge variant="secondary" className="text-xs">
                                {tool.duration_ms.toFixed(1)}ms
                              </Badge>
                            </div>
                            {tool.error && (
                              <p className="text-red-600 mt-1">Error: {tool.error}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <p className="text-xs opacity-60 mt-2">
                      {new Date(message.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                </div>

                {message.type === 'user' && (
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <User className="h-5 w-5 text-blue-600" />
                    </div>
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex gap-3 justify-start">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                  <Bot className="h-5 w-5 text-amber-600" />
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-amber-600" />
                    <span className="text-sm text-gray-600">Hermes is thinking...</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input Area */}
        <div className="border-t p-4">
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask Hermes anything..."
              disabled={loading}
              className="flex-1"
              data-testid="hermes-input"
            />
            <Button
              onClick={() => handleSendMessage()}
              disabled={!input.trim() || loading}
              className="gap-2"
              data-testid="hermes-send-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Send
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default HermesChat;
